import Select, { StylesConfig } from '@databases/atlaskit/Select';
import { useFlaggingCallback } from '@databases/banners';
import FieldName from '@databases/field-name/FieldName';
import ValidationStatus from '@databases/form/ValidationStatus';
import { messages } from '@databases/intl/messages';
import { DatabaseField } from '@databases/types/database';
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl-next';

const formatOptionLabel = (value: DatabaseField): React.ReactNode => {
  return <FieldName field={value} showIcon={true} />;
};

const getOptionValue = (value: DatabaseField) => {
  return value.id;
};

const getOptionLabel = (value: DatabaseField) => {
  return value.name;
};

const styles: StylesConfig<DatabaseField> = {
  menu: (base) => ({ ...base, width: 'max-content', minWidth: '100%' }),
};

function validateField(
  field: DatabaseField | undefined,
  isRequired: boolean | undefined
): boolean {
  return !(isRequired && !field);
}

interface FieldPickerProps {
  fields?: ReadonlyArray<DatabaseField>;
  value?: DatabaseField | undefined;
  onChange: (value: DatabaseField | undefined) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  isRequired?: boolean;
  isClearable?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  spacing?: 'compact' | 'default';
  isDisabled?: boolean;
  isInvalid?: boolean;
}

/**
 * Renders a select box that allows to select a field from the given database.
 */
const FieldPicker: React.FunctionComponent<FieldPickerProps> = ({
  value,
  fields,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  isRequired,
  isClearable,
  isLoading,
  placeholder,
  spacing,
  isDisabled = false,
  isInvalid,
}) => {
  const { formatMessage } = useIntl();

  useEffect(() => {
    // If a different database is selected, reset the value
    if (
      (!fields && value) ||
      (fields && value && !fields.some((field) => field.id === value.id))
    ) {
      onChange(undefined);
    }
  }, [fields, onChange, value]);

  const handleChange = useFlaggingCallback(
    (val: DatabaseField | null) => {
      onChange(val ?? undefined);
    },
    [onChange]
  );

  return (
    <ValidationStatus
      error={
        validateField(value, isRequired)
          ? undefined
          : formatMessage(messages['validation.required-field'])
      }
    >
      {(props) => (
        <Select<DatabaseField>
          {...props}
          value={value ?? null}
          onChange={handleChange}
          isClearable={isClearable}
          isDisabled={isDisabled || !fields}
          formatOptionLabel={formatOptionLabel}
          options={fields}
          getOptionValue={getOptionValue}
          getOptionLabel={getOptionLabel}
          onFocus={onFocus}
          onBlur={onBlur}
          autoFocus={autoFocus}
          isLoading={isLoading}
          placeholder={placeholder}
          spacing={spacing}
          styles={styles}
          isInvalid={props.isInvalid || isInvalid}
          classNamePrefix="field-picker"
        />
      )}
    </ValidationStatus>
  );
};

export default FieldPicker;
